import React, { useEffect, useState } from 'react';

import ModalBoolean from '@components/modals/ModalBoolean';
import { useAppSelector } from '@core/hooks/appHooks';
import useDidMountEffect from '@core/hooks/useDidMountEffect';
import useDetectedChangesBlocker from '@core/hooks/useUnsavedChanges';
import { NetworkService } from '@core/services/networkService/networkService';
import { selectVendor } from '@core/store/appSlice/appSlice';
import DemmiCard from '@demmi-ui/Card/Card';
import DemmiIconButton from '@demmi-ui/IconButton';
import { demmiRequest } from '@helpers/app.helper';
import { ProductsHelper } from '@helpers/products.helper';
import { DemmiFS } from '@subhanhabib/demmilib';

import InventoryProductVariantInputs from './_inputs';

interface Props {
  variant: DemmiFS.ProductVariant;
  onUpdate: (variant: DemmiFS.ProductVariant) => void;
  onClickDelete: () => void;
}

const InventoryProductVariantsFC: React.FC<Props> = ({
  variant,
  onUpdate,
  onClickDelete,
}) => {
  const CSSBlock = 'product-variant';
  const vendor = useAppSelector(selectVendor);
  const [name, setName] = useState<string>('');
  const [minQuantity, setMinQuantity] = useState<number>(0);
  const [availableQuantity, setAvailableQuantity] = useState<number>(0);
  const [pricePerUnit, setPricePerUnit] = useState<number>(0);
  const [increments, setIncrements] = useState<number>(1);
  const [unit, setUnit] = useState<DemmiFS.UnitsHelper.WeightUnit>(
    DemmiFS.UnitsHelper.WeightUnit.undefined,
  );
  // if unit === "weight_each"
  const [weightPerItem, setWeightPerItem] = useState<number>();
  const [unitPerItem, setUnitPerItem] =
    useState<DemmiFS.UnitsHelper.WeightUnit>();

  const [detectedChanges, setDetectedChanges] = useState(false);
  const [isValid, setIsValid] = useState(false);

  useDetectedChangesBlocker({
    detectedChanges,
  });

  const setValues = (variant: DemmiFS.ProductVariant) => {
    setName(variant.name);
    setMinQuantity(variant.minQuantity);
    setAvailableQuantity(variant.availableQuantity);
    setPricePerUnit(variant.pricePerUnit);
    setIncrements(variant.increments);
    setUnit(variant.unit);
    if (variant.weightPerItem) setWeightPerItem(variant.weightPerItem);
    if (variant.unitPerItem) setUnitPerItem(variant.unitPerItem);
  };

  useEffect(() => {
    setValues(variant);
  }, [variant]);

  useDidMountEffect(() => {
    setDetectedChanges(
      ProductsHelper.variantHasChanges(variant, {
        name,
        availableQuantity,
        minQuantity,
        increments,
        pricePerUnit,
        unit,
        weightPerItem,
        unitPerItem,
      }),
    );
    setIsValid(
      ProductsHelper.isProductVariantValid({
        name,
        availableQuantity,
        minQuantity,
        increments,
        pricePerUnit,
        unit,
        weightPerItem,
        unitPerItem,
      }),
    );
  }, [
    variant,
    name,
    availableQuantity,
    minQuantity,
    increments,
    pricePerUnit,
    unit,
    weightPerItem,
    unitPerItem,
  ]);

  const discardChanges = () => setValues(variant);

  const saveChanges = async () => {
    if (name.length > 0) {
      const v: DemmiFS.ProductVariant = {
        docID: variant.docID,
        productID: variant.productID,
        name,
        minQuantity,
        availableQuantity,
        pricePerUnit,
        increments,
        unit,
        weightPerItem,
        unitPerItem,
      };

      demmiRequest(NetworkService.ProductVariants.updateProductVariant(v)).then(
        () => onUpdate(v),
      );
    }
  };

  return (
    <DemmiCard className={`${CSSBlock}`}>
      <InventoryProductVariantInputs
        currencyCode={DemmiFS.CurrencyHelper.getCurrencySymbolFromCountryCode(
          vendor?.address?.countryCode ?? '',
        )}
        name={name}
        setName={setName}
        minQuantity={minQuantity}
        setMinQuantity={setMinQuantity}
        availableQuantity={availableQuantity}
        setAvailableQuantity={setAvailableQuantity}
        pricePerUnit={pricePerUnit}
        setPricePerUnit={setPricePerUnit}
        increments={increments}
        setIncrements={setIncrements}
        unit={unit}
        setUnit={setUnit}
        weightPerItem={weightPerItem}
        setWeightPerItem={setWeightPerItem}
        unitPerItem={unitPerItem}
        setUnitPerItem={setUnitPerItem}
      />
      <div className={`${CSSBlock}__buttons-row`}>
        <ModalBoolean
          onClose={() => {}}
          onConfirm={onClickDelete}
          titleText={`Are you sure you want to delete`}
          itemText={variant.name}
          triggerButtonText={'Delete'}
          triggerButtonIcon={'fa-box-archive'}
          triggerButtonVariant={'delete'}
        />
        <DemmiIconButton
          faIcon="fa-ban"
          disabled={
            variant.docID !== ProductsHelper.NEW_PRODUCT_VARIANT_TEMP_ID &&
            !detectedChanges
          }
          onClick={discardChanges}
        />
        <DemmiIconButton
          faIcon="fa-regular fa-floppy-disk"
          disabled={!detectedChanges || !isValid}
          onClick={saveChanges}
        />
      </div>
    </DemmiCard>
  );
};

export default InventoryProductVariantsFC;
